import React from 'react';
import PropTypes from 'prop-types';
import { Box, Button, IconButton, Tooltip, Typography } from '@mui/material';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import AddIcon from '@mui/icons-material/Add';
import CenterFocusStrongOutlinedIcon from '@mui/icons-material/CenterFocusStrongOutlined';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import LabelOutlinedIcon from '@mui/icons-material/LabelOutlined';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import RemoveIcon from '@mui/icons-material/Remove';
import SatelliteAltOutlinedIcon from '@mui/icons-material/SatelliteAltOutlined';
import TimelineOutlinedIcon from '@mui/icons-material/TimelineOutlined';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';

const circuloSx = (ativo) => ({
  width: 36,
  height: 36,
  p: 0,
  bgcolor: ativo ? 'warning.main' : 'background.paper',
  color: ativo ? 'common.white' : 'text.primary',
  border: 1,
  borderColor: ativo ? 'warning.main' : 'divider',
  boxShadow: 1,
  '&:hover': { bgcolor: ativo ? 'warning.dark' : 'background.paper' },
});

const fatiaSx = (ativo, borda) => ({
  width: 36,
  height: 36,
  p: 0,
  borderRadius: 0,
  bgcolor: ativo ? 'warning.main' : 'background.paper',
  color: ativo ? 'common.white' : 'text.primary',
  borderLeft: borda ? 1 : 0,
  borderColor: 'divider',
  '&:hover': { bgcolor: ativo ? 'warning.dark' : 'action.hover' },
});

function Controle({ title, ativo, onClick, children, borda, circulo }) {
  return (
    <Tooltip title={title}>
      <IconButton
        aria-label={title}
        aria-pressed={ativo}
        onClick={onClick}
        sx={circulo ? circuloSx(ativo) : fatiaSx(ativo, borda)}
      >
        {children}
      </IconButton>
    </Tooltip>
  );
}

Controle.propTypes = {
  title: PropTypes.string.isRequired,
  ativo: PropTypes.bool,
  onClick: PropTypes.func,
  children: PropTypes.node.isRequired,
  borda: PropTypes.bool,
  circulo: PropTypes.bool,
};

Controle.defaultProps = {
  ativo: false,
  onClick: undefined,
  borda: false,
  circulo: false,
};

function Pilula({ label, children }) {
  return (
    <Box
      role="group"
      aria-label={label}
      sx={{
        display: 'flex',
        borderRadius: '18px',
        overflow: 'hidden',
        border: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
        boxShadow: 1,
      }}
    >
      {children}
    </Box>
  );
}

Pilula.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

const KmzMapaControles = ({
  edicao,
  modo,
  onModo,
  texto,
  tom,
  mostrarTudo,
  onMostrarTudo,
  onMostrarSelecionado,
  trocaTrecho,
  basemap,
  onBasemap,
  fullScreen,
  onToggleFullScreen,
  mostrarInfo,
  onMostrarInfo,
  onCentralizar,
  onZoomIn,
  onZoomOut,
  onCancelar,
}) => (
  <>
    {edicao ? (
      <Box
        sx={{
          position: 'absolute',
          top: 12,
          left: 12,
          zIndex: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Controle
          circulo
          title="Adicionar trecho"
          ativo={modo === 'adicionar'}
          onClick={() => onModo('adicionar')}
        >
          <AddIcon fontSize="small" />
        </Controle>
        <Controle
          circulo
          title="Remover trecho"
          ativo={modo === 'remover'}
          onClick={() => onModo('remover')}
        >
          <RemoveIcon fontSize="small" />
        </Controle>
        {modo !== 'nenhum' && (
          <Button
            size="small"
            variant="outlined"
            onClick={onCancelar}
            sx={{ bgcolor: 'background.paper' }}
          >
            Cancelar
          </Button>
        )}
        {texto && (
          <Typography
            variant="caption"
            sx={{
              bgcolor: 'background.paper',
              color: tom === 'erro' ? 'error.dark' : tom === 'ok' ? 'success.dark' : 'text.primary',
              border: 1,
              borderColor: tom === 'erro' ? 'error.main' : tom === 'ok' ? 'success.main' : 'divider',
              borderRadius: 1,
              boxShadow: 1,
              px: 1,
              py: 0.5,
              maxWidth: 340,
            }}
          >
            {texto}
          </Typography>
        )}
      </Box>
    ) : null}
    <Box
      sx={{
        position: 'absolute',
        right: 12,
        bottom: 12,
        zIndex: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        flexWrap: 'wrap',
        gap: 1,
        maxWidth: 'calc(100% - 24px)',
      }}
    >
      {trocaTrecho ? (
        <Pilula label="O que o mapa mostra">
          <Controle title="Mostrar tudo" ativo={mostrarTudo} onClick={onMostrarTudo}>
            <AccountTreeOutlinedIcon fontSize="small" />
          </Controle>
          <Controle title="Mostrar selecionado" ativo={!mostrarTudo} borda onClick={onMostrarSelecionado}>
            <TimelineOutlinedIcon fontSize="small" />
          </Controle>
        </Pilula>
      ) : null}
      <Pilula label="Mapa base">
        <Controle title="Satélite" ativo={basemap === 'satellite'} onClick={() => onBasemap('satellite')}>
          <SatelliteAltOutlinedIcon fontSize="small" />
        </Controle>
        <Controle title="Ruas" ativo={basemap === 'ruas'} borda onClick={() => onBasemap('ruas')}>
          <MapOutlinedIcon fontSize="small" />
        </Controle>
      </Pilula>
      <Controle
        circulo
        title={mostrarInfo ? 'Ocultar informações' : 'Mostrar informações'}
        ativo={mostrarInfo}
        onClick={onMostrarInfo}
      >
        <LabelOutlinedIcon fontSize="small" />
      </Controle>
      <Controle
        circulo
        title={fullScreen ? 'Sair da tela cheia' : 'Tela cheia'}
        ativo={fullScreen}
        onClick={onToggleFullScreen}
      >
        {fullScreen ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
      </Controle>
      <Pilula label="Câmera">
        <Controle title="Centralizar no trecho" onClick={onCentralizar}>
          <CenterFocusStrongOutlinedIcon fontSize="small" />
        </Controle>
        <Controle title="Afastar" borda onClick={onZoomOut}>
          <ZoomOutIcon fontSize="small" />
        </Controle>
        <Controle title="Aproximar" borda onClick={onZoomIn}>
          <ZoomInIcon fontSize="small" />
        </Controle>
      </Pilula>
    </Box>
  </>
);

KmzMapaControles.propTypes = {
  edicao: PropTypes.bool,
  modo: PropTypes.oneOf(['nenhum', 'adicionar', 'remover']).isRequired,
  onModo: PropTypes.func.isRequired,
  texto: PropTypes.string,
  tom: PropTypes.oneOf(['passo', 'erro', 'ok']),
  mostrarTudo: PropTypes.bool.isRequired,
  onMostrarTudo: PropTypes.func.isRequired,
  onMostrarSelecionado: PropTypes.func.isRequired,
  trocaTrecho: PropTypes.bool,
  basemap: PropTypes.oneOf(['satellite', 'ruas']).isRequired,
  onBasemap: PropTypes.func.isRequired,
  fullScreen: PropTypes.bool.isRequired,
  onToggleFullScreen: PropTypes.func.isRequired,
  mostrarInfo: PropTypes.bool.isRequired,
  onMostrarInfo: PropTypes.func.isRequired,
  onCentralizar: PropTypes.func.isRequired,
  onZoomIn: PropTypes.func.isRequired,
  onZoomOut: PropTypes.func.isRequired,
  onCancelar: PropTypes.func,
};

KmzMapaControles.defaultProps = {
  edicao: true,
  texto: '',
  tom: 'passo',
  onCancelar: undefined,
  trocaTrecho: true,
};

export default KmzMapaControles;
